import React from 'react';
import { useDispatch } from 'react-redux';
import { Dialog } from 'radix-ui';

import { CloseDrawer } from '@actions/drawer';
import { cn } from '@components/lib/utils';

import useDrawer from '../hooks/useDrawer';

/* Global slide-over drawer. The content + open state live in Redux (see
 * OpenDrawer/CloseDrawer); this component only renders them. We use Radix
 * Dialog rather than a hand-rolled CSS transition because Radix mounts the
 * content in the closed state and flips to open on the next frame, so the
 * enter animation actually plays (a plain class toggle skips it).
 *
 * `modal={false}`: no overlay, no scroll-lock, no focus-trap — matches the
 * previous non-modal panel and avoids breaking the modals the drawer content
 * opens on top of itself (payments, remove-creator, etc.). */
const Drawer: React.FC = () => {
  const dispatch = useDispatch();
  const { display, children, style } = useDrawer();

  return (
    <Dialog.Root
      open={display}
      modal={false}
      onOpenChange={(open) => {
        if (!open) dispatch(CloseDrawer());
      }}
    >
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          // Keep current behaviour: don't close on outside click (the drawer
          // content has its own close control + route changes close it).
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          style={style ?? undefined}
          className={cn(
            'fixed inset-y-0 right-0 z-[100] flex h-full w-full max-w-[937px] flex-col overflow-y-auto',
            'bg-card shadow-[-4px_0_24px_rgba(0,0,0,0.1)] outline-none',
            'duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
          )}
        >
          <Dialog.Title className="sr-only">Details</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Drawer;
